import React from 'react';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Switch, Button, Card, Row, Col, Select } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { ALL_DISH_CATEGORIES, CatalogSource, DishType } from '@wellverse/shared-ts';
import { LocalizedContentTabs, normalizeTranslations } from '../../components/LocalizedContentTabs';

const LANG_FIELDS = [
  { key: 'name', label: 'Name', placeholder: 'e.g. Char Siu Rice', required: true },
  { key: 'description', label: 'Description', textarea: true, rows: 2 },
];

export const DishCatalogCreate: React.FC = () => {
  const { formProps, saveButtonProps } = useForm({ resource: 'dish-catalog' });

  return (
    <Create resource="dish-catalog" saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: any) =>
          formProps.onFinish?.({
            ...values,
            translations: normalizeTranslations(values.translations, LANG_FIELDS),
          })
        }
      >
        <Card title="Basic Info" size="small" style={{ marginBottom: 24 }}>
          <LocalizedContentTabs fields={LANG_FIELDS} showSharedDivider={false} />
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Type" name="type" initialValue={DishType.DISH}>
                <Select
                  options={Object.values(DishType).map((t) => ({ label: t, value: t }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                <Select
                  placeholder="Select category"
                  allowClear
                  showSearch
                  options={ALL_DISH_CATEGORIES.map((c) => ({
                    label: c.replace(/_/g, ' '),
                    value: c,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Standard Portion (g)" name="standardPortionGrams" rules={[{ required: true }]} initialValue={100}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Aliases" name="aliases">
            <Select mode="tags" placeholder="e.g. BBQ Pork Rice, 叉烧饭" />
          </Form.Item>
          <Form.Item label="Image URL" name="imageUrl">
            <Input placeholder="https://..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Source" name="source" initialValue={CatalogSource.MANUAL}>
                <Select placeholder="Select source">
                  <Select.Option value={CatalogSource.MANUAL}>{CatalogSource.MANUAL}</Select.Option>
                  <Select.Option value={CatalogSource.USDA}>{CatalogSource.USDA}</Select.Option>
                  <Select.Option value={CatalogSource.CFS}>{CatalogSource.CFS}</Select.Option>
                  <Select.Option value={CatalogSource.AI_GENERATED}>{CatalogSource.AI_GENERATED}</Select.Option>
                  <Select.Option value={CatalogSource.WELLCOME}>{CatalogSource.WELLCOME}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Nutrition (auto-calculated from ingredients, or override)" size="small" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Calories" name="calories" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Protein (g)" name="protein" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Fat (g)" name="fat" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Carbs (g)" name="carbs" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Fiber (g)" name="fiber">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Sodium (mg)" name="sodium">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Ingredients" size="small" style={{ marginBottom: 24 }}>
          <Form.List name="ingredients">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'displayName']} rules={[{ required: true }]} noStyle>
                        <Input placeholder="Display name" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'ingredientCatalogId']} noStyle>
                        <Input placeholder="Catalog ID (optional)" />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item {...restField} name={[name, 'weightGrams']} rules={[{ required: true }]} noStyle>
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="Weight (g)" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...restField} name={[name, 'percentage']} noStyle>
                        <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.01} placeholder="%" />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Ingredient
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Variations" size="small" style={{ marginBottom: 24 }}>
          <Form.List name="variations">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'name']} rules={[{ required: true }]} noStyle>
                        <Input placeholder="Variation name" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...restField} name={[name, 'deltaCalories']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ Cal" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaProtein']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ P" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaFat']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ F" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaCarbs']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ C" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaSodium']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ Na" />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Variation
                </Button>
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </Create>
  );
};
